import React, { useMemo } from 'react';
import { Box, Button, Chip, CircularProgress } from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AiGenerateButton from './AiGenerateButton';

interface Props {
  courseId: number;
  nodeId: number;
  content: string;
  isInherited: boolean;
  hasCustomContent: boolean;
  onChange: (html: string) => void;
  onSave: () => void;
  isSaving: boolean;
  onAiGenerate?: (courseId: number, nodeId: number) => void;
}

const TOOLBAR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ script: 'sub' }, { script: 'super' }],
    ['image', 'link', 'video'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
};

export default function TopicContentEditor({
  courseId,
  nodeId,
  content,
  isInherited,
  hasCustomContent,
  onChange,
  onSave,
  isSaving,
  onAiGenerate,
}: Props) {
  const [showPreview, setShowPreview] = React.useState(false);

  const inheritanceBadge = useMemo(() => {
    if (hasCustomContent) {
      return <Chip label="Custom" color="success" size="small" sx={{ mb: 1 }} />;
    }
    if (isInherited) {
      return <Chip label="Inherited from library" color="warning" size="small" sx={{ mb: 1 }} />;
    }
    return null;
  }, [isInherited, hasCustomContent]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        {inheritanceBadge}
        <Box sx={{ flexGrow: 1 }} />
        {onAiGenerate && (
          <AiGenerateButton
            courseId={courseId}
            nodeId={nodeId}
            onDraftReceived={onChange}
          />
        )}
        <Button
          size="small"
          variant="outlined"
          onClick={() => setShowPreview((p) => !p)}
        >
          {showPreview ? 'Edit' : 'Student view'}
        </Button>
      </Box>

      {showPreview ? (
        <Box
          dangerouslySetInnerHTML={{ __html: content }}
          sx={{ typography: 'body1', lineHeight: 1.8, minHeight: 200, border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}
        />
      ) : (
        <ReactQuill
          theme="snow"
          value={content}
          onChange={onChange}
          modules={TOOLBAR_MODULES}
          style={{ minHeight: 240 }}
        />
      )}

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
}
